/**
 * ConsoleCommandCenter — the single mount point for the console's global
 * overlays: the ⌘K command palette, the ⌘I AI-assist panel, and the
 * toast region. Rendered once by ``ConsoleShell`` (inside the console theme
 * provider) so every surface reaches them.
 *
 * It owns the two keyboard controllers (⌘K / ⌘I), tracks the recent
 * destinations the palette shows on an empty query, and resolves the
 * context-aware label the assistant is opened with. The nav catalog is
 * passed IN (from ``ConsoleShell``'s VISIBLE-section projection — already
 * capability-gated, so a Sviluppatore's palette never lists Membri /
 * Fatturazione) to keep one source of truth without a runtime import cycle.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Cable, KeyRound, Sparkles, Zap } from "lucide-react";
import * as React from "react";

import { useCommandPaletteShortcut } from "@ando/ui";

import { pushConsoleRecent, readConsoleRecents } from "../lib/commandPalette";
import { AssistPanel } from "./AssistPanel";
import {
  type CommandActionItem,
  type CommandNavItem,
  ConsoleCommandPalette,
} from "./CommandPalette";
import { ConToaster } from "./ConToaster";
import type { ConsoleNavItem } from "./ConsoleShell";

// Message descriptors hoisted above the component so ``useMemo`` deps stay stable.
const assistantLabel = msg`Apri l'assistente AI`;
const newKeyLabel = msg`Crea una AppKey`;
const connectLabel = msg`Collega un connettore`;
const connectorActionsLabel = msg`Gestisci le azioni dei connettori`;
const consoleLabel = msg`Console`;

export function ConsoleCommandCenter({
  navItems,
}: {
  navItems: readonly ConsoleNavItem[];
}): React.ReactElement {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const palette = useCommandPaletteShortcut(); // ⌘K
  const assist = useCommandPaletteShortcut({ key: "i" }); // ⌘I

  const [recents, setRecents] = React.useState<string[]>(() => readConsoleRecents());

  // Record every visited console path as a recent (dedupe, capped).
  React.useEffect(() => {
    if (pathname && pathname.startsWith("/console")) {
      setRecents(pushConsoleRecent(pathname));
    }
  }, [pathname]);

  const resolvedNav: CommandNavItem[] = React.useMemo(
    () => navItems.map((item) => ({ to: item.to, label: i18n._(item.label), icon: item.icon })),
    [navItems, i18n],
  );

  // Recents → known destinations, current page excluded (you're already here).
  const recentsDisplay: CommandNavItem[] = React.useMemo(() => {
    const byPath = new Map(resolvedNav.map((n) => [n.to, n]));
    return recents
      .filter((path) => path !== pathname)
      .map((path) => byPath.get(path))
      .filter((n): n is CommandNavItem => n !== undefined);
  }, [recents, pathname, resolvedNav]);

  const onNavigate = React.useCallback(
    (to: string): void => {
      void navigate({ to: to as never });
    },
    [navigate],
  );

  const actions: CommandActionItem[] = React.useMemo(
    () => [
      {
        id: "assist",
        label: i18n._(assistantLabel),
        icon: Sparkles,
        kbd: "⌘I",
        keywords: ["ai", "assistente", "chiedi", "compila"],
        run: () => assist.setOpen(true),
      },
      {
        id: "new-key",
        label: i18n._(newKeyLabel),
        icon: KeyRound,
        keywords: ["appkey", "api", "token"],
        run: () => onNavigate("/console/keys"),
      },
      {
        id: "connect",
        label: i18n._(connectLabel),
        icon: Cable,
        keywords: ["connettore", "sorgente", "integrazione"],
        run: () => onNavigate("/console/connectors"),
      },
      {
        // The write-loop cockpit is otherwise reachable only via
        // Connettori → Personalizzati → a row's "Azioni": deep-link the
        // Personalizzati tab so the actions surface is discoverable here too.
        id: "connector-actions",
        label: i18n._(connectorActionsLabel),
        icon: Zap,
        keywords: ["azioni", "connettore", "write-loop", "webhook", "push"],
        run: () =>
          void navigate({ to: "/console/connectors", search: { view: "custom" } } as never),
      },
    ],
    [i18n, assist, onNavigate, navigate],
  );

  // Context-aware assistant eyebrow — the section you opened it from.
  const contextLabel = React.useMemo(() => {
    const match = resolvedNav
      .filter((n) => pathname === n.to || pathname.startsWith(`${n.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return match?.label ?? i18n._(consoleLabel);
  }, [resolvedNav, pathname, i18n]);

  return (
    <>
      <ConsoleCommandPalette
        open={palette.open}
        onClose={palette.close}
        navItems={resolvedNav}
        actions={actions}
        recents={recentsDisplay}
        onNavigate={onNavigate}
      />
      <AssistPanel open={assist.open} onClose={assist.close} contextLabel={contextLabel} />
      <ConToaster />
    </>
  );
}
